import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Button, Upload, message, Space, Alert, List, Modal } from 'antd';
import { UploadOutlined, FilePdfOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { VehicleHistoryReport } from '../../types/vehicle'; // Import the central type
import { authPpiApiAxiosWithRefresh } from '../../services/api';

const { Title, Text } = Typography;
const { confirm } = Modal;

interface HistoryReportReviewProps {
  docId: string;
  vin: string;
  initialReports: VehicleHistoryReport[];
  onSubmit: (reports: VehicleHistoryReport[]) => void;
  onBack: () => void;
}

const HistoryReportReview: React.FC<HistoryReportReviewProps> = ({
  docId,
  vin,
  initialReports,
  onSubmit,
  onBack
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<VehicleHistoryReport[]>(initialReports);
  const [isScraping, setIsScraping] = useState(true);
  const [isBuyReportModalVisible, setIsBuyReportModalVisible] = useState(false);


  const fetchReports = useCallback(async () => {
    try {
      const response = await (await authPpiApiAxiosWithRefresh()).get(`/check-history-reports/${docId}`);
      // This endpoint now returns a simple array of URLs
      if (response.data.success) {
        const reportUrls: string[] = response.data.reports || [];
        // Transform the array of URLs into an array of VehicleHistoryReport objects
        const reportObjects: VehicleHistoryReport[] = reportUrls.map(url => ({ url }));
        setReports(reportObjects);
        setIsScraping(false);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setIsScraping(false);
    }
  }, [docId]);

  useEffect(() => {
    const poll = setInterval(() => {
      if (isScraping) {
        fetchReports();
      } else {
        clearInterval(poll);
      }
    }, 1000);

    return () => clearInterval(poll);
  }, [isScraping, fetchReports]);

  const handleDelete = async (reportUrl: string) => {
    confirm({
      title: 'Are you sure you want to delete this report?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      async onOk() {
        try {
          const response = await (await authPpiApiAxiosWithRefresh()).delete(`/history-report/${docId}`, {
            data: { report_url: reportUrl }
          });
          

          if (response.data.success) {
            message.success('Report deleted successfully');
            setReports(prevReports => prevReports.filter(report => report.url !== reportUrl));
          } else {
            throw new Error(response.data.error || 'Failed to delete report');
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'An unknown error occurred.';
          message.error(`Failed to delete report: ${msg}`);
        }
      },
    });
  };

  const handleUploadAndContinue = async () => {
    if (fileList.length === 0) {
      onSubmit(reports);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      fileList.forEach(file => {
        if (file.originFileObj) {
          formData.append('files', file.originFileObj);
        }
      });

      const response = await (await authPpiApiAxiosWithRefresh()).post(`/upload-history-reports/${docId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        message.success('History reports uploaded successfully');
        await fetchReports(); 
        onSubmit(reports);
      } else {
        throw new Error(response.data.error || 'Failed to upload history reports');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while uploading');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Boost Accuracy with a Vehicle History Report</Title>
          <Text type="secondary">
          Pacer’s AI recognizes reports from CARFAX, AutoCheck, and more. We‘ve included reports for eligible vehicles for you in this demo.          </Text>
        </div>

        {isScraping && <Alert message="Searching for history reports..." type="info" showIcon />}

        {reports.length > 0 && (
          <List
            bordered
            dataSource={reports}
            renderItem={(report) => (
              <List.Item
                actions={[
                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(report.url)} />
                ]}
              >
                <List.Item.Meta
                  avatar={<FilePdfOutlined />}
                  title={<a href={report.url} target="_blank" rel="noopener noreferrer">{report.url}</a>}
                  description="Online Report"
                />
              </List.Item>
            )}
          />
        )}

        {!isScraping && reports.length === 0 && (
          <Alert
            message="We didn't detect a History Report"
            description={
              <span>
                <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                Adding a history report (CarFax, AutoCheck, or VinAudit) makes your inspection more accurate. These reports reveal accident records, title changes, and other hidden issues that aren’t visible in photos or listings. If you already have one, upload it below. If not, you can continue without it — but we highly recommend including one for the best results.
              </span>
            }
            type="warning"
            showIcon
          />
        )}

        {error && <Alert message="Error" description={error} type="error" showIcon />}

        <div>
          <Title level={5}>Upload Additional Reports</Title>
          <Upload
            accept=".pdf"
            fileList={fileList}
            onChange={({ fileList: newFileList }) => setFileList(newFileList)}
            multiple
            beforeUpload={() => false}
          >
            <Button icon={<UploadOutlined />}>Upload History Report (PDF)</Button>
          </Upload>
          {!isScraping && reports.length === 0 && (
          <div style={{ marginTop: '8px' }}>
            <Text>
              Don't have one?{' '}
              <Button type="link" onClick={() => setIsBuyReportModalVisible(true)} style={{ padding: 0 }}>
                Buy a report here.
              </Button>
            </Text>
          </div>
        )}
        </div>

        

        <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button onClick={onBack}>Back</Button>
          <Button 
            type="primary" 
            onClick={handleUploadAndContinue}
            loading={loading}
          >
            {fileList.length > 0  ? 'Upload & Continue' : reports.length > 0 ? 'Continue' : 'Continue Without History Report'}
          </Button>
        </Space>
      </Space>
      <Modal
        title="Buy a Vehicle History Report"
        visible={isBuyReportModalVisible}
        onCancel={() => setIsBuyReportModalVisible(false)}
        footer={null}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {vin ? (
            <Text>
              You can purchase a report from one of these providers using your vehicle's VIN.
            </Text>
          ) : (
            <>
              <Text>
                A VIN is required to purchase a history report. Please contact the seller to obtain the vehicle's VIN.
              </Text>
              <Alert
                message="Tip: Once you have the VIN, you can also go back to the 'Vehicle Details' page to check for theft or salvage records."
                type="info"
                showIcon
              />
            </>
          )}
          {vin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text strong>Your VIN:</Text>
              <Text code copyable>
                {vin}
              </Text>
            </div>
          )}
          <Text>To save a report, you may need to “print to PDF” in order to save it to your computer.</Text>
          <Text>Click a provider below to purchase a report:</Text>
          <Space>
            <Button
              onClick={() => window.open(`https://www.carfax.com/vehicle-history-reports/?vin=${vin}`, '_blank')}
            >
              Buy on CarFax
            </Button>
            <Button
              onClick={() => window.open(`https://www.vinaudit.com/vin-search?vin=${vin}`, '_blank')}
            >
              Buy on VinAudit
            </Button>
            <Button
              onClick={() => window.open(`https://www.autocheck.com/vehiclehistory/landing?vin=${vin}`, '_blank')}
            >
              Buy on AutoCheck
            </Button>
          </Space>
          <Text type="secondary" italic style={{ fontSize: '12px', textAlign: 'center', display: 'block', width: '100%' }}>
            We DO NOT earn commission from purchases made through these links.
          </Text>
        </Space>
      </Modal>
    </Card>
  );
};

export default HistoryReportReview;